import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FloatingActionButton, Icon } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { GALLERY_IMAGES, SOCIAL_LINKS } from '../../constants';
import { getGoogleDriveFileId, getGoogleDriveImageUrl, getGoogleDriveHighResUrl } from '../../utils/helpers';

/**
 * Photos Page - Instagram-style grid layout
 * Automatically detects image orientation:
 * - Landscape images → full width row
 * - Portrait/square images → 2 per row
 */
const Photos = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [imageDimensions, setImageDimensions] = useState({});
  const [loadingComplete, setLoadingComplete] = useState(false);

  // Page-specific meta information
  const pageTitle = "Photo Gallery | Matisa - Singer & Guitarist";
  const pageDescription = "Browse Matisa's photo gallery - performance shots, event highlights, and downloadable banners for your events.";

  // Get preview URL from Google Drive
  const getPreviewUrl = (image) => {
    if (image.driveUrl) {
      const fileId = getGoogleDriveFileId(image.driveUrl);
      if (fileId) return getGoogleDriveImageUrl(fileId);
    }
    // Fallback to S3 URL
    return image.url;
  };

  // Get high-res URL for lightbox from Google Drive
  const getHighResUrl = (image) => {
    if (image.driveUrl) {
      const fileId = getGoogleDriveFileId(image.driveUrl);
      if (fileId) return getGoogleDriveHighResUrl(fileId);
    }
    // Fallback to S3 URL
    return image.url;
  };

  // Load image dimensions on mount
  useEffect(() => {
    const loadImageDimensions = async () => {
      const dimensions = {};
      
      await Promise.all(
        GALLERY_IMAGES.map((image) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              dimensions[image.id] = {
                width: img.naturalWidth,
                height: img.naturalHeight,
                isLandscape: img.naturalWidth > img.naturalHeight,
              };
              resolve();
            };
            img.onerror = () => {
              // Default to portrait on error
              dimensions[image.id] = {
                width: 0,
                height: 0,
                isLandscape: false,
              };
              resolve();
            };
            // Use Google Drive preview URL for dimension detection
            img.src = getPreviewUrl(image);
          });
        })
      );
      
      setImageDimensions(dimensions);
      setLoadingComplete(true);
    };
    
    loadImageDimensions();
  }, []);

  // Get effective size based on image dimensions or manual override
  const getImageSize = useCallback((image) => {
    // If size is manually specified, use it as override
    if (image.size) {
      return image.size;
    }
    // Auto-detect based on dimensions
    const dims = imageDimensions[image.id];
    if (dims) {
      return dims.isLandscape ? 'large' : 'medium';
    }
    // Default to medium while loading
    return 'medium';
  }, [imageDimensions]);

  // Get object-position based on focus setting
  const getFocusClass = (focus) => {
    switch (focus) {
      case 'top':
        return 'object-top';
      case 'bottom':
        return 'object-bottom';
      case 'cover':
        return 'object-contain bg-[#1a212e]';
      case 'center':
      default:
        return 'object-center';
    }
  };

  // Organize images into rows to prevent gaps
  // Landscape images get their own row, portrait images are paired
  const imageRows = useMemo(() => {
    if (!loadingComplete) return [];
    
    const rows = [];
    let mediumBuffer = [];
    
    GALLERY_IMAGES.forEach((image) => {
      const size = getImageSize(image);
      
      if (size === 'large') {
        // Flush any buffered medium images first
        if (mediumBuffer.length > 0) {
          rows.push({ type: 'medium-pair', images: [...mediumBuffer] });
          mediumBuffer = [];
        }
        // Add large image as its own row
        rows.push({ type: 'large', images: [image] });
      } else {
        // Buffer medium images
        mediumBuffer.push(image);
        // When we have 2, create a row
        if (mediumBuffer.length === 2) {
          rows.push({ type: 'medium-pair', images: [...mediumBuffer] });
          mediumBuffer = [];
        }
      }
    });
    
    // Handle remaining medium image (if odd number)
    if (mediumBuffer.length === 1) {
      rows.push({ type: 'single-medium', images: mediumBuffer });
    }
    
    return rows;
  }, [loadingComplete, getImageSize]);

  // Open lightbox
  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = '';
  };

  // Navigate to previous/next image
  const navigateImage = (direction) => {
    const currentIndex = GALLERY_IMAGES.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : GALLERY_IMAGES.length - 1;
    } else {
      newIndex = currentIndex < GALLERY_IMAGES.length - 1 ? currentIndex + 1 : 0;
    }
    setSelectedImage(GALLERY_IMAGES[newIndex]);
  };

  // Handle image load error
  const handleImageError = (imageId) => {
    setImageErrors(prev => ({ ...prev, [imageId]: true }));
  };

  // Handle direct download to device using S3 URL
  const handleDownload = async (e, image) => {
    e.stopPropagation();
    
    // Get filename from S3 URL
    const urlParts = image.url.split('?')[0];
    const originalFileName = urlParts.split('/').pop();
    const fileExtension = originalFileName.split('.').pop().toLowerCase();
    const filename = `matisa-photo-${image.id}.${fileExtension}`;
    
    try {
      // Fetch directly from S3 (requires CORS to be configured on S3 bucket)
      const response = await fetch(image.url, {
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open S3 URL in new tab (user can right-click save)
      window.open(image.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Render a single image card
  const ImageCard = ({ image, aspectClass }) => {
    const hasError = imageErrors[image.id];
    const dims = imageDimensions[image.id];
    // Use Google Drive URL for preview
    const previewUrl = getPreviewUrl(image);
    
    return (
      <button
        onClick={() => openLightbox(image)}
        className={`
          relative w-full overflow-hidden rounded-lg bg-[#1a212e]
          transition-all duration-200 hover:opacity-90 active:scale-[0.98]
          focus:outline-none focus:ring-2 focus:ring-primary
          ${aspectClass}
        `}
      >
        {!hasError ? (
          <img
            src={previewUrl}
            alt={`Gallery item ${image.id}`}
            loading="lazy"
            className={`
              w-full h-full object-cover
              ${getFocusClass(image.focus || 'top')}
            `}
            onError={() => handleImageError(image.id)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            <Icon name="broken_image" size="xl" />
          </div>
        )}
        
        {/* Debug badge - shows detected orientation */}
        {dims && (
          <div className="absolute bottom-1 right-1 text-[8px] font-bold text-white/50 bg-black/30 px-1 rounded">
            {dims.isLandscape ? 'L' : 'P'}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/photos" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header with Menu */}
      <PageHeader title="Matisa" />

      {/* Main Content Scroll Area */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Page Title */}
        <section className="p-4 pb-2">
          <h1 className="text-white text-2xl font-bold leading-tight tracking-tight">
            Photo Gallery
          </h1>
          <p className="text-[#90a9cb] text-xs font-medium mt-1">
            Tap to view full size • {GALLERY_IMAGES.length} photos
          </p>
        </section>

        {/* Instagram-style Grid - Row based layout */}
        <section className="px-2 pb-24">
          {!loadingComplete ? (
            // Loading skeleton
            <div className="flex flex-col gap-2">
              <div className="w-full aspect-[4/3] bg-[#1a212e] rounded-lg animate-pulse" />
              <div className="grid grid-cols-2 gap-2">
                <div className="aspect-[3/4] bg-[#1a212e] rounded-lg animate-pulse" />
                <div className="aspect-[3/4] bg-[#1a212e] rounded-lg animate-pulse" />
              </div>
              <div className="w-full aspect-[4/3] bg-[#1a212e] rounded-lg animate-pulse" />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {imageRows.map((row, rowIndex) => {
                if (row.type === 'large') {
                  // Full width large image (landscape)
                  return (
                    <div key={`row-${rowIndex}`} className="w-full">
                      <ImageCard 
                        image={row.images[0]} 
                        aspectClass="aspect-[4/3]"
                      />
                    </div>
                  );
                } else if (row.type === 'medium-pair') {
                  // Two medium images side by side (portraits)
                  return (
                    <div key={`row-${rowIndex}`} className="grid grid-cols-2 gap-2">
                      {row.images.map((image) => (
                        <ImageCard 
                          key={image.id}
                          image={image} 
                          aspectClass="aspect-[3/4]"
                        />
                      ))}
                    </div>
                  );
                } else if (row.type === 'single-medium') {
                  // Single medium image (last one if odd number)
                  return (
                    <div key={`row-${rowIndex}`} className="w-full">
                      <ImageCard 
                        image={row.images[0]} 
                        aspectClass="aspect-[4/3]"
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Footer / Social Link */}
          <div className="mt-8 flex justify-center">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-[#90a9cb] transition-colors hover:text-primary"
            >
              <span className="text-sm font-semibold">More on Instagram</span>
              <Icon name="open_in_new" size="sm" className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </section>
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-10">
            {/* Image Counter */}
            <div className="text-white/70 text-sm font-medium">
              {GALLERY_IMAGES.findIndex(img => img.id === selectedImage.id) + 1} / {GALLERY_IMAGES.length}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Download Button */}
              <button
                onClick={(e) => handleDownload(e, selectedImage)}
                className="flex items-center gap-2 px-3 py-2 text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Download image"
              >
                <Icon name="download" size="sm" />
                <span className="text-sm font-medium">Download</span>
              </button>

              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
                aria-label="Close"
              >
                <Icon name="close" size="lg" />
              </button>
            </div>
          </div>

          {/* Full Size Image - uses high-res Google Drive URL */}
          <div 
            className="relative w-full h-full flex items-center justify-center p-4 pt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getHighResUrl(selectedImage)}
              alt={`Gallery item ${selectedImage.id}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Previous image"
          >
            <Icon name="chevron_left" size="xl" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Next image"
          >
            <Icon name="chevron_right" size="xl" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Photos;
